<template>
  <div id="SectionNetZeroFAQs">
    <UIContainer class="SectionNetZeroFAQs__container">
      <template #left>
        <SectionTitle>
          <template #title>FAQs</template>
          <template #description>
            Have more questions about Net Zero? Can’t find the answer you’re
            looking for? Please
            <UIButtonText
              class="SectionNetZeroFAQs__support-link"
              :to="{ name: 'Contact' }"
            >
              contact our support team.
            </UIButtonText>
          </template>
        </SectionTitle>
      </template>

      <template #right>
        <UIFaqs :faqs="faqs" />
      </template>
    </UIContainer>
  </div>
</template>

<script setup lang="ts">
import SectionTitle from '@/views/components/misc/SectionTitle.vue'
import UIButtonText from '@/views/components/ui/buttons/UIButtonText.vue'
import UIContainer from '@/views/components/ui/containers/UIContainer/UIContainer.vue'
import UIFaqs from '@/views/components/features/faqs/UIFaqs.vue'

const faqs = [
  {
    question: 'What is net zero software?',
    answer:
      'Net zero software is a digital platform that helps organisations measure, monitor, and reduce greenhouse gas (GHG) emissions across Scope 1, 2, and 3. By combining advanced analytics, carbon accounting protocols, and data visualisation, it aligns with the GHG Protocol and Science Based Targets initiative (SBTi) to support credible target setting. It also facilitates ESRS- and CSRD-aligned disclosures, enabling transparent reporting and progress toward verified net zero goals.',
  },
  {
    question: 'What are the benefits of net zero software?',
    answer:
      'It streamlines precise GHG calculation across all scopes in line with the GHG Protocol, improving compliance with CSRD/ESRS and voluntary initiatives like SBTi. Automation and analytics reduce manual work and errors. Scenario analysis and optimisation highlight cost-effective abatement pathways, while transparent reporting strengthens stakeholder trust and access to sustainable finance.',
  },
  {
    question:
      "How does Sustainalyse Carbon Management Platform help companies reduce their emissions?",
    answer:
      "Sustainalyse platform collects data across operations and the value chain, calculates emissions using certified methodologies aligned with the latest scientific standards, and pinpoints high-emission areas for action. Through scenario analysis, automated reporting, and real-time tracking, companies can evaluate the impact of science-based targets, granular reduction actions, and supply chain improvements. It also ensures ESRS/CSRD-ready workflows for guided, streamlined reporting, supported by expert guidance to drive accountability and long-term results.",
  },
  {
    question: 'What are the main features of net zero software?',
    answer:
      'Core capabilities include emissions tracking for Scope 1, 2, and 3, scenario analysis, and sustainability reporting. Integration with the GHG Protocol ensures accurate calculations, while ESRS/CSRD-aligned reports help communicate progress credibly. Advanced tools often include supply chain collaboration to support collective reductions across the value chain.',
  },
  {
    question: 'Why is net zero important for companies?',
    answer:
      "Net zero strategies help mitigate climate risks, comply with evolving regulations, and unlock economic and reputational benefits. Aligning with a 1.5°C pathway and policies such as the EU's Green Deal and CSRD reduces physical, financial, and operational risks. Net zero also drives innovation and efficiency, lowering costs over time and strengthening competitive positioning.",
  },
]
</script>

<style lang="scss" scoped>
#SectionNetZeroFAQs {
  background-color: $color-white;
  .SectionNetZeroFAQs__container {
    :deep(.UIContainer__grid) {
      display: flex;
      flex-direction: column;
      align-items: stretch;

      @include md {
        flex-direction: row;
        align-items: normal;
      }

      .UIContainer__left {
        width: 100%;
        @include md {
          width: 40%;
          padding-right: 3rem;
        }
      }
      .UIContainer__right {
        width: 100%;
        margin-top: 1rem;
        @include md {
          width: 60%;
          margin-top: 0;
        }
      }
    }
  }

  .SectionNetZeroFAQs__support-link {
    @include font-size-18px();
    color: $color-ui-gray-cool-600;
    text-decoration: underline;
    cursor: pointer;
  }
}
</style>
